import { CssTextAlign } from 'gf-types';
import React, { PureComponent } from 'react';
import { FieldTypes } from '../../../../helpers/constants';
import { getRgbaColorString } from '../../../../helpers/helpers';
import { ValueChangedForFieldFunc } from '../../../formeditor-types';
import { SendAFormInteractive, withSendAFormInteractiveState } from '../../SendAFormInteractiveState';

export type InputFieldProps = {
  valueUpdated: ValueChangedForFieldFunc;
  id?: string;
  fieldId?: string;
  fieldType?: number;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  align?: CssTextAlign;
  foregroundColor?: TypeMePlease;
  backgroundColor?: TypeMePlease;
  focusColor: TypeMePlease;
  padding?: number;
  fontName?: string;
  fontSize?: number;
  lineHeight?: number;
  editable?: boolean;
  value?: string;
  tabIndex?: number;
  characterLimit?: number;
} & SendAFormInteractive;

class InputField extends PureComponent<InputFieldProps, TypeMePlease> {
  constructor(props) {
    super(props);

    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);

    this.state = { focused: false, value: this.props.value };
  }

  componentWillUnmount() {
    if (this.props.editable && this.state.focused) {
      this.props.valueUpdated({ value: this.state.value });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ value: this.props.value });
    }
  }

  onBlur() {
    this.setState({ focused: false });
    // when interactive is false, then we're presenting this
    // component from the send a from v2 preview
    if (this.props.editable && this.props.interactive) {
      this.props.valueUpdated({ value: this.state.value });
    }
  }

  onFocus() {
    if (!this.props.interactive) {
      return;
    }

    this.setState({ focused: true });
  }

  onKeyDown(e) {
    if (e.keyCode === 13 && e.target.tagName.toLowerCase() === 'input') {
      e.target.blur();
    }
  }

  onChange(e) {
    if (this.props.fieldType === FieldTypes.Number && e.target.value !== '' && !e.target.value.match(/^[0-9,!@#$%^&*()_+:;<>?/=\-.]+$/)) {
      return;
    }
    this.setState({ value: e.target.value });
  }

  onClick(e: React.MouseEvent) {
    // interactive is true when we're in the normal form editor view
    if (this.props.interactive) {
      return;
    }

    // only handle for send a form v2
    e.preventDefault();
    e.stopPropagation();
    this.props.onSendAFormFieldClick(this.props.fieldId, 'form');
  }

  render() {
    const x = this.props.x;
    const y = this.props.y;
    const w = this.props.w;
    const h = this.props.h;

    const align = this.props.align;

    const backgroundColor = this.state.focused ? this.props.focusColor : this.props.backgroundColor;
    const border = !this.props.sendAFormSelected ? '2px dashed #000' : undefined;

    const inputStyle: React.CSSProperties = {
      width: `${w}px`,
      height: `${h}px`,
      left: `${x}px`,
      top: `${y}px`,
      textAlign: align,
      fontFamily: this.props.fontName,
      fontSize: this.props.fontSize,
      color: getRgbaColorString(this.props.foregroundColor),
      paddingLeft: `${this.props.padding}px` || '0px',
      paddingRight: `${this.props.padding}px` || '0px',
      paddingTop: undefined,
      paddingBottom: undefined,
      background: undefined,
      lineHeight: undefined,
      border: this.props.interactive ? undefined : border
    };

    if (this.props.fieldType === FieldTypes.TextArea) {
      inputStyle.paddingTop = `${this.props.padding}px` || '0px';
      inputStyle.paddingBottom = `${this.props.padding}px` || '0px';
    }

    // when interactive is false, then we're presenting this
    // component from the send a from v2 preview
    if (this.props.editable || !this.props.interactive) {
      inputStyle.background = this.props.sendAFormSelected ? this.props.highlightColor : getRgbaColorString(backgroundColor);
    }

    if (this.props.fieldType === FieldTypes.TextArea) {
      if (this.props.lineHeight) {
        inputStyle.lineHeight = `${this.props.lineHeight}px`;
      }
      return (
        <textarea
          id={this.props.id}
          data-fieldid={this.props.fieldId}
          style={inputStyle}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          onChange={this.onChange}
          onClick={this.onClick}
          value={this.state.value}
          // when interactive is false, then we're presenting this
          // component from the send a from v2 preview
          readOnly={!this.props.editable || !this.props.interactive}
          tabIndex={this.props.editable ? this.props.tabIndex : -1}
          maxLength={this.props.characterLimit}
        />
      );
    }

    return (
      <input
        id={this.props.id}
        data-fieldid={this.props.fieldId}
        type="text"
        style={inputStyle}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onKeyDown={this.onKeyDown}
        onChange={this.onChange}
        onClick={this.onClick}
        value={this.state.value}
        // when interactive is false, then we're presenting this
        // component from the send a from v2 preview
        readOnly={!this.props.editable || !this.props.interactive}
        tabIndex={this.props.editable ? this.props.tabIndex : -1}
        autoComplete="stop-autocomplete"
        maxLength={this.props.characterLimit}
      />
    );
  }
}

export default withSendAFormInteractiveState(InputField);
